import { useState, type ReactNode } from 'react';
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Typography,
  type ListItemButtonProps,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetCatalogoQuery, useGetLojaAtualQuery, useGetMeQuery } from '../api';
import type { RootState } from '../store';
import { limparTokens } from '../store/auth-slice';
import { alternarModoTema } from '../store/tema-slice';
import { urlAvatarUsuario } from '../lib/avatar';
import { montarLinkInstagram, montarLinkWhatsapp } from '../lib/contato';
import { getTabletConfig } from '../lib/tablet-config';
import {
  abrirChamarGarcom,
  abrirConta,
  abrirPromocoes,
  ESTILO_BOTAO_CONTA,
  ESTILO_BOTAO_GARCOM,
} from '../lib/tablet-eventos';
import { canalDoIdentificador, ehDispositivoFixo, identificacaoSalao } from '../lib/canal';
import { contextoSalao } from '../lib/salao';
import { destinoChamadoConfigurado } from '../lib/destino-chamado';
import { DialogLogin } from './dialog-login';
import { usePedidosLocais } from '../lib/pedidos-locais';

/**
 * Gatilho da conta para o cabeçalho/barra fixa: avatar (DiceBear) quando
 * logado, ou ícone de pessoa quando deslogado. Sempre abre o {@link MenuLateral}.
 *
 * `rotuloDeslogado`: quando informado, exibe um botão com texto (ex.: "Acessar")
 * no lugar do ícone enquanto o cliente não estiver logado.
 */
export function GatilhoConta({
  aoAbrir,
  rotuloDeslogado,
}: {
  aoAbrir: () => void;
  rotuloDeslogado?: string;
}): JSX.Element {
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const logado = Boolean(accessToken);
  const { data: usuario } = useGetMeQuery(undefined, { skip: !logado });

  if (!logado) {
    if (rotuloDeslogado) {
      return (
        <Button variant="outlined" size="small" startIcon={<LoginIcon />} onClick={aoAbrir}>
          {rotuloDeslogado}
        </Button>
      );
    }
    return (
      <IconButton aria-label="Entrar" color="primary" onClick={aoAbrir}>
        <LoginIcon />
      </IconButton>
    );
  }
  // Primeiro nome ao lado do avatar deixa explícito quem está logado — só a
  // partir do sm: no celular o nome disputa a linha com o nome da loja, e o
  // avatar já identifica quem está logado.
  const primeiroNome = usuario?.nome?.trim().split(/\s+/)[0];
  return (
    <IconButton aria-label="Minha conta" onClick={aoAbrir} sx={{ p: 0.5, borderRadius: 4 }}>
      <Avatar src={urlAvatarUsuario(usuario?.email)} sx={{ width: 32, height: 32, bgcolor: 'grey.100' }} />
      {primeiroNome && (
        <Typography
          variant="body2"
          noWrap
          sx={{ display: { xs: 'none', sm: 'block' }, ml: 0.75, mr: 0.5, maxWidth: 100, fontWeight: 500 }}
        >
          {primeiroNome}
        </Typography>
      )}
    </IconButton>
  );
}

/** Item da gaveta: ícone cinza à esquerda, seta discreta à direita. */
function ItemMenu({
  icone,
  texto,
  fim,
  ...props
}: { icone: ReactNode; texto: string; fim?: ReactNode } & ListItemButtonProps & {
    href?: string;
    target?: string;
    rel?: string;
  }): JSX.Element {
  return (
    <ListItemButton sx={{ px: 2.5, py: 1.5 }} {...props}>
      <ListItemIcon sx={{ minWidth: 44, color: 'text.secondary' }}>{icone}</ListItemIcon>
      <ListItemText primary={texto} primaryTypographyProps={{ color: 'text.primary' }} />
      {fim ?? <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
    </ListItemButton>
  );
}

/**
 * Menu lateral (drawer esquerdo) do cardápio online: identidade do usuário
 * (avatar DiceBear + nome/e-mail) ou atalho para entrar, últimos pedidos e
 * navegação da conta. Não é usado no TOTEM (autoatendimento anônimo).
 */
export function MenuLateral({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }): JSX.Element {
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const logado = Boolean(accessToken);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: usuario } = useGetMeQuery(undefined, { skip: !logado });
  const { data: loja } = useGetLojaAtualQuery();
  // Promoções: item do menu em qualquer canal (o catálogo já está em cache, a
  // consulta aqui não vai à rede de novo).
  const canal = loja ? canalDoIdentificador(loja.identificador) : undefined;
  const { data: catalogo } = useGetCatalogoQuery(canal!, { skip: !canal });
  const temPromocoes = !!catalogo?.grupos.some((g) => g.produtos.some((p) => p.emPromocao));
  // Atalhos de salão (tablet/mesa/comanda): chamar garçom + ver total.
  const ctxSalao = contextoSalao(loja);
  const mostrarAtalhosSalao = ctxSalao.ativo;
  // "Chamar garçom" só com mesa FÍSICA conhecida (some no QR de comanda).
  const mostrarChamarGarcom = ctxSalao.mesaFisica != null && destinoChamadoConfigurado(loja);
  const rotuloLocal = loja ? identificacaoSalao(loja.identificador, getTabletConfig().mesa).rotulo : '';
  // Login/conta só no delivery — mesa/comanda/totem são anônimos.
  const ehDelivery = loja?.identificador.tipo === 'DELIVERY';
  // Sem login, "Meus pedidos" abre quando há pedido feito neste aparelho.
  const pedidosLocais = usePedidosLocais(loja?.configuracao.id);
  const whatsapp = loja?.configuracao.whatsapp?.trim();
  const instagram = loja?.configuracao.instagram?.trim();
  const modoTema = useSelector((s: RootState) => s.tema.mode);
  // Tablet e totem: o tema é do operador, e a escolha de um cliente ficaria para o próximo
  const mostrarTema = !!loja && !ehDispositivoFixo(loja.identificador);

  const [loginAberto, setLoginAberto] = useState(false);

  const irPara = (rota: string): void => {
    aoFechar();
    navigate(rota);
  };
  // Login sem sair do cardápio: fecha a gaveta e abre o modal de login. Ao
  // concluir, o token entra na store e o menu reflete o usuário logado.
  const abrirLogin = (): void => {
    aoFechar();
    setLoginAberto(true);
  };
  const sair = (): void => {
    aoFechar();
    dispatch(limparTokens());
    navigate('/catalogo');
  };
  // Tablet: atalhos do menu (mesmo diálogo do FAB / da conta).
  const chamarGarcom = (): void => {
    aoFechar();
    abrirChamarGarcom();
  };
  const verConta = (): void => {
    aoFechar();
    abrirConta();
  };
  const verPromocoes = (): void => {
    aoFechar();
    abrirPromocoes();
  };

  return (
    <>
    <Drawer anchor="left" open={aberto} onClose={aoFechar} PaperProps={{ sx: { width: 320, maxWidth: '88vw' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 56,
          px: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <IconButton aria-label="Fechar menu" color="primary" onClick={aoFechar}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      {mostrarAtalhosSalao && (
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{ width: 48, height: 48, bgcolor: 'primary.main', color: 'primary.contrastText' }}
              >
                <TableRestaurantIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                  Você está na
                </Typography>
                <Typography variant="h6" noWrap>
                  {rotuloLocal}
                </Typography>
              </Box>
            </Stack>
            {mostrarChamarGarcom && (
              <Button {...ESTILO_BOTAO_GARCOM} startIcon={<RoomServiceIcon />} onClick={chamarGarcom}>
                Chamar garçom
              </Button>
            )}
            <Button {...ESTILO_BOTAO_CONTA} startIcon={<ReceiptLongIcon />} onClick={verConta}>
              Ver a conta
            </Button>
          </Stack>
        </Box>
      )}

      {ehDelivery && logado && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
          <Avatar src={urlAvatarUsuario(usuario?.email)} sx={{ width: 48, height: 48, bgcolor: 'grey.100' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {usuario?.nome ?? 'Minha conta'}
            </Typography>
            {usuario?.email && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {usuario.email}
              </Typography>
            )}
          </Box>
        </Stack>
      )}

      <List sx={{ py: 1 }}>
        {/* Entrar é a ação que mais importa para quem não está logado: fundo suave na cor da marca. */}
        {ehDelivery && !logado && (
          <ListItemButton
            onClick={abrirLogin}
            sx={{
              mx: 1.5,
              my: 1,
              px: 1.5,
              py: 1.5,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.07),
              '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText
              primary="Entrar ou cadastrar"
              secondary="Histórico de pedidos e cartões salvos"
              primaryTypographyProps={{ color: 'primary.main', fontWeight: 600 }}
            />
            <ChevronRightIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemButton>
        )}
        {ehDelivery && (
          <ItemMenu
            icone={<ReceiptLongOutlinedIcon />}
            texto="Meus pedidos"
            onClick={() => irPara('/meus-pedidos')}
            disabled={!logado && pedidosLocais.length === 0}
          />
        )}
        {ehDelivery && logado && (
          <ItemMenu icone={<PersonOutlineIcon />} texto="Minha conta" onClick={() => irPara('/conta')} />
        )}
        {temPromocoes && <ItemMenu icone={<LocalOfferOutlinedIcon />} texto="Promoções" onClick={verPromocoes} />}
      </List>

      {(whatsapp || instagram) && (
        <List
          subheader={
            <Typography variant="overline" color="text.secondary" display="block" sx={{ px: 2.5, pt: 1 }}>
              Fale com a loja
            </Typography>
          }
        >
          {whatsapp && (
            <ItemMenu
              icone={<WhatsAppIcon />}
              texto="WhatsApp"
              component="a"
              href={montarLinkWhatsapp(whatsapp)}
              target="_blank"
              rel="noopener"
              onClick={aoFechar}
            />
          )}
          {instagram && (
            <ItemMenu
              icone={<InstagramIcon />}
              texto="Instagram"
              component="a"
              href={montarLinkInstagram(instagram)}
              target="_blank"
              rel="noopener"
              onClick={aoFechar}
            />
          )}
        </List>
      )}

      {(mostrarTema || (ehDelivery && logado)) && (
        <List sx={{ mt: 'auto', pb: 2 }}>
          {mostrarTema && (
            <ItemMenu
              icone={<DarkModeOutlinedIcon />}
              texto="Modo escuro"
              onClick={() => dispatch(alternarModoTema())}
              fim={
                <Switch
                  edge="end"
                  checked={modoTema === 'dark'}
                  tabIndex={-1}
                  inputProps={{ 'aria-label': 'Modo escuro' }}
                />
              }
            />
          )}
          {ehDelivery && logado && (
            <ItemMenu icone={<LogoutIcon />} texto="Sair" onClick={sair} fim={<span />} />
          )}
        </List>
      )}
    </Drawer>

    {/* Fora do Drawer: a gaveta desmonta seu conteúdo ao fechar; o modal de
        login precisa sobreviver a isso (abrimos logo após fechar a gaveta). */}
    <DialogLogin aberto={loginAberto} onFechar={() => setLoginAberto(false)} onSucesso={() => setLoginAberto(false)} />
    </>
  );
}
